/**
 * SERP-V1 transport — `/api/v1/search`, its taste shelf, and the profile status.
 *
 * Every type here comes from `@/api/contract/api-v1.types`, which is generated
 * from the backend's OpenAPI schema. Nothing in this file re-declares a shape:
 * a contract change breaks `vue-tsc` here rather than surfacing as an empty
 * drawer in a smoke test.
 *
 * NO TRANSLATION LAYER. The frontend speaks the contract's own names —
 * `categories` carries NAMES, colour facets carry `hex`, facets are plural.
 * An adapter that renamed them would be a second source of truth for the shape,
 * and the two would drift the first time the backend added a facet.
 */

import { apiV1Client } from '@/api/api'
import type { components } from '@/api/contract/api-v1.types'

export type SearchV1Response = components['schemas']['SearchV1Response']
export type SearchFilters = components['schemas']['SearchFilters']
export type SearchContext = components['schemas']['SearchContext']
export type SearchSort = components['schemas']['SearchSort']
export type SearchFacets = components['schemas']['SearchFacets']
export type CategoryFacet = components['schemas']['CategoryFacet']
export type CategoryFacetGroup = components['schemas']['CategoryFacetGroup']
export type CategoryFacetNode = components['schemas']['CategoryFacetNode']
export type PriceFacet = components['schemas']['PriceFacet']
export type ValueFacet = components['schemas']['ValueFacet']
export type ContextEcho = components['schemas']['ContextEcho']
export type SimilarRequest = components['schemas']['SimilarRequest']
export type YouMayLikeResponse = components['schemas']['YouMayLikeResponse']
export type PersonalizationStatusResponse =
  components['schemas']['PersonalizationStatusResponse']
export type ProductTeaser = components['schemas']['ProductTeaser']

export interface SearchV1Request {
  context: SearchContext
  filters?: SearchFilters
  sort?: SearchSort
  personalized?: boolean
  /** Opaque. Absent on the first page; echoed back from `results.cursor`. */
  cursor?: string | null
  limit?: number
}

/**
 * One results request for every entry flow (text, image, text+image,
 * find-similar). POST, not GET: the body carries a nested filter object and a
 * price range, and a URL encoding all of it would be past what a CDN caches.
 */
export async function fetchSearchV1(
  request: SearchV1Request,
  options?: { signal?: AbortSignal },
): Promise<SearchV1Response> {
  const { data } = await apiV1Client.post<SearchV1Response>('search', request, {
    signal: options?.signal,
  })
  return data
}

export type SearchCountResponse = components['schemas']['SearchCountResponse']

export interface SearchCountRequest {
  context: SearchContext
  filters?: SearchFilters
  /** Carried because dislikes REMOVE documents — it can change the number. */
  personalized?: boolean
}

/**
 * How many results the drawer's PENDING selection would show.
 *
 * The button says "Show N results" about a selection the shopper has not
 * applied yet, so N cannot come from the response on screen — that one counts
 * what is already applied. It also cannot be estimated locally: facet counts
 * are each computed WITHOUT their own axis (block B's law), so they describe
 * single axes and never the intersection, and a button promising 42 over a
 * page of 17 is a lie in the interface.
 *
 * The body is the search's body minus what cannot change a count — no `sort`,
 * no `cursor`, no `limit` — so the same object goes to both endpoints and the
 * two answers agree by construction.
 */
export async function fetchSearchCount(
  request: SearchCountRequest,
  options?: { signal?: AbortSignal },
): Promise<SearchCountResponse> {
  const { data } = await apiV1Client.post<SearchCountResponse>(
    'search/count',
    request,
    { signal: options?.signal },
  )
  return data
}

/**
 * The taste shelf — its OWN request, fired in parallel with the grid.
 *
 * `personalization.applied` is false whenever taste did not actually reach the
 * query: signed out, a profile too thin to rank with, or a failure. The shelf
 * is then a quality-ordered fallback and the caller hides it rather than
 * dressing it up.
 */
export async function fetchYouMayLike(
  params: { size?: number; audience?: string; query?: string } = {},
  options?: { signal?: AbortSignal },
): Promise<YouMayLikeResponse> {
  // V3 (session 18): on a search page the shelf's pool is that page's pool —
  // the query rides along; omitted (not sent empty) on the home page
  const { query, ...rest } = params
  const { data } = await apiV1Client.get<YouMayLikeResponse>('search/you-may-like', {
    params: query && query.trim() ? { ...rest, query: query.trim() } : rest,
    signal: options?.signal,
  })
  return data
}

/**
 * What the Personalized badge's popover draws.
 *
 * The `score` here and `personalization.score` in the search response are the
 * SAME integer from the same service. The frontend renders both and computes
 * neither.
 */
export async function fetchPersonalizationStatus(options?: {
  signal?: AbortSignal
}): Promise<PersonalizationStatusResponse> {
  const { data } = await apiV1Client.get<PersonalizationStatusResponse>(
    'personalization/status',
    { signal: options?.signal },
  )
  return data
}

export type SearchImageRegion = components['schemas']['ImageRegion']
export type UploadSearchImageResponse =
  components['schemas']['src__domain__usecases__search_v1__upload_search_image__UploadSearchImageUseCase__Response']

export interface UploadSearchImageOptions {
  /** The crop the CLIENT made, as fractions of the parent photo (0..1, inside it). */
  region?: SearchImageRegion | null
  /** The `image_id` of the photo the crop was cut from. */
  parentImageId?: string | null
}

/**
 * `POST /v1/search/images` (BE-105…107, BE-126) — the photo (or the crop the client already
 * made) for `context.image_id`. Stored exactly as `/images/upload_img` stores it, so the
 * `image_id` is also what `/images/get-image-parts` reads. `region` and `parent_image_id`
 * are echoed back for the client to forward on the search's context, where the SEARCH
 * event records them; this call records nothing and crops nothing.
 */
export async function uploadSearchImage(
  image: File | Blob,
  options: UploadSearchImageOptions = {},
): Promise<UploadSearchImageResponse> {
  const form: Record<string, File | Blob | string> = { img: image }
  if (options.region) form.region = JSON.stringify(options.region)
  if (options.parentImageId) form.parent_image_id = options.parentImageId
  const { data } = await apiV1Client.postForm<UploadSearchImageResponse>('search/images', form)
  return data
}
